import { Authentication } from '@hulu/react-sso';
import { lazy } from 'react';

import { Permissions } from '../../apis/graphql/types';
import { PermittedPage, User } from '../../components';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { RoutePath } from '../../constants';
import Report from '../../pages/baymax/Report';
import { StyledCampaignReportsWrapper } from '../../pages/Campaigns/Summary/CampaignSummary/CampaignReports/styled';
import { RouteProps } from '../../routes/types';
import lazyRetry from '../../utils/lazyFetch';

const AccountManagement = lazy(() =>
  lazyRetry(() => import('../../pages/AccountManagement/AccountManager'), 'AccountManager')
);
const AdGallery = lazy(() => lazyRetry(() => import('../../pages/AdGallery'), 'AdGallery'));
const OAuthCallback = lazy(() =>
  lazyRetry(() => import('../../pages/Admin/OAuthCallback/OAuthCallback'), 'OAuthCallback')
);
const Campaigns = lazy(() => lazyRetry(() => import('../../pages/Campaigns'), 'Campaigns'));
const CampaignReview = lazy(() => lazyRetry(() => import('../../pages/Campaigns/CampaignReview'), 'CampaignReview'));
const CampaignForm = lazy(() => lazyRetry(() => import('../../pages/Campaigns/Form/CampaignForm'), 'CampaignForm'));
const CreateCampaign = lazy(() =>
  lazyRetry(() => import('../../pages/Campaigns/Form/CreateCampaign'), 'CreateCampaign')
);
const LineItemEditForm = lazy(() =>
  lazyRetry(() => import('../../pages/Campaigns/Form/LineItemEditForm'), 'LineItemEditForm')
);
const PaymentPage = lazy(() => lazyRetry(() => import('../../pages/Campaigns/PaymentPage'), 'PaymentPage'));
const SuccessPage = lazy(() => lazyRetry(() => import('../../pages/Campaigns/SuccessPage'), 'SuccessPage'));
const Notifications = lazy(() => lazyRetry(() => import('../../pages/Notifications'), 'Notifications'));
const UserOnboarding = lazy(() => lazyRetry(() => import('../../pages/UserOnboarding'), 'UserOnboarding'));
const CampaignReports = lazy(() =>
  lazyRetry(() => import('../../pages/Campaigns/Summary/CampaignSummary/CampaignReports'), 'CampaignReports')
);
const MixedReports = lazy(() =>
  lazyRetry(
    () =>
      import(
        '../../pages/Campaigns/Summary/CampaignSummary/CampaignReports/MixedCampaignReportsPage/MixedCampaignReportsPage'
      ),
    'MixedReports'
  )
);
const CampaignSummary = lazy(() =>
  lazyRetry(() => import('../../pages/Campaigns/Summary/CampaignSummary'), 'CampaignSummary')
);
const LineItemSummary = lazy(() =>
  lazyRetry(() => import('../../pages/Campaigns/Summary/LineItemSummary'), 'LineItemSummary')
);
const LateLineItems = lazy(() => lazyRetry(() => import('../../pages/baymax/LateLineItems'), 'LateLineItems'));

const reportsRoutes = [`${RoutePath.REPORTS}/:adEntityId/*`, `${RoutePath.REPORTS}/*`];

const LEGACY_BRUNO_REPORT_ROUTE = `/:adAccountId${RoutePath.REPORTS}/*`;
const LEGACY_BAYMAX_REPORT_ROUTE = `/:organizationId/report-template/:reportTemplateId/report/*`;

export const getBrunoSpecificRoutes = (permissions: Permissions): RouteProps[] => [
  {
    path: RoutePath.ONBOARDING,
    element: <ProtectedRoute children={<UserOnboarding />} />,
  },
  {
    path: `${RoutePath.ACCOUNT}/*`,
    element: <ProtectedRoute children={<AccountManagement />} />,
  },
  {
    path: `/:adAccountId${RoutePath.CAMPAIGNS}`,
    element: (
      <ProtectedRoute
        children={
          <PermittedPage permitted={permissions.READ_CAMPAIGN_LINEITEMS}>
            <Campaigns />
          </PermittedPage>
        }
      />
    ),
  },
  {
    path: `${RoutePath.AD_GALLERY}/*`,
    element: (
      <ProtectedRoute
        children={
          <PermittedPage permitted={permissions.READ_CREATIVES}>
            <AdGallery />
          </PermittedPage>
        }
      />
    ),
  },
  ...reportsRoutes.map(
    (path): RouteProps => ({
      path,
      element: (
        <ProtectedRoute
          children={
            <PermittedPage permitted={permissions.READ_REPORT}>
              <StyledCampaignReportsWrapper>
                <CampaignReports />
              </StyledCampaignReportsWrapper>
            </PermittedPage>
          }
        />
      ),
    })
  ),
  {
    path: LEGACY_BRUNO_REPORT_ROUTE,
    element: (
      <ProtectedRoute
        children={
          <PermittedPage permitted={permissions.READ_REPORT}>
            <StyledCampaignReportsWrapper>
              <CampaignReports />
            </StyledCampaignReportsWrapper>
          </PermittedPage>
        }
      />
    ),
  },
  {
    path: `${RoutePath.REPORTS}/:adEntityId/report-template/:reportTemplateId/report/*`,
    element: (
      <ProtectedRoute>
        <PermittedPage
          permitted={permissions.READ_ORGANIZATION && permissions.BUILD_REPORT && permissions.READ_REPORT_TEMPLATE}
        >
          <Report />
        </PermittedPage>
      </ProtectedRoute>
    ),
  },
  {
    path: LEGACY_BAYMAX_REPORT_ROUTE,
    element: (
      <ProtectedRoute>
        <PermittedPage
          permitted={permissions.READ_ORGANIZATION && permissions.BUILD_REPORT && permissions.READ_REPORT_TEMPLATE}
        >
          <Report />
        </PermittedPage>
      </ProtectedRoute>
    ),
  },
  {
    path: `${RoutePath.REPORTS}/:adEntityId/late-lines`,
    element: (
      <ProtectedRoute>
        <PermittedPage
          permitted={permissions.READ_ORGANIZATION && permissions.BUILD_REPORT && permissions.READ_REPORT_TEMPLATE}
        >
          <LateLineItems />
        </PermittedPage>
      </ProtectedRoute>
    ),
  },
  {
    path: `/${RoutePath.REPORTS}/mixed/*`,
    element: (
      <ProtectedRoute
        children={
          <PermittedPage permitted={permissions.READ_REPORT}>
            <MixedReports />
          </PermittedPage>
        }
      />
    ),
  },
  {
    path: `/:adAccountId${RoutePath.CAMPAIGN_CREATE}`,
    element: (
      <ProtectedRoute
        children={
          <PermittedPage permitted={permissions.GET_CAMPAIGN && permissions.CREATE_LINEITEM}>
            <CreateCampaign />
          </PermittedPage>
        }
      />
    ),
  },
  {
    path: `/:adAccountId${RoutePath.CAMPAIGNS}/:campaignId`,
    element: (
      <ProtectedRoute
        children={
          <PermittedPage permitted={permissions.GET_CAMPAIGN && permissions.READ_CAMPAIGN_LINEITEMS}>
            <CampaignForm />
          </PermittedPage>
        }
      />
    ),
  },
  {
    path: `/:adAccountId${RoutePath.CAMPAIGNS}/:campaignId/lineItem/:lineItemId`,
    element: (
      <ProtectedRoute
        children={
          <PermittedPage permitted={permissions.GET_CAMPAIGN && permissions.READ_CAMPAIGN_LINEITEMS}>
            <LineItemEditForm />
          </PermittedPage>
        }
      />
    ),
  },
  {
    path: `/:adAccountId${RoutePath.CAMPAIGNS}/:campaignId/review`,
    element: (
      <ProtectedRoute
        children={
          <PermittedPage permitted={permissions.GET_CAMPAIGN && permissions.READ_CAMPAIGN_LINEITEMS}>
            <CampaignReview />
          </PermittedPage>
        }
      />
    ),
  },
  {
    path: `/:adAccountId${RoutePath.CAMPAIGNS}/:campaignId/payment`,
    element: (
      <ProtectedRoute
        children={
          <PermittedPage permitted={permissions.SUBMIT_LINE_ITEM}>
            <PaymentPage />
          </PermittedPage>
        }
      />
    ),
  },
  {
    path: `/:adAccountId${RoutePath.CAMPAIGNS}/:campaignId/success`,
    element: (
      <ProtectedRoute
        children={
          <PermittedPage permitted={permissions.GET_CAMPAIGN && permissions.READ_CAMPAIGN_LINEITEMS}>
            <SuccessPage />
          </PermittedPage>
        }
      />
    ),
  },
  {
    path: `/:adAccountId${RoutePath.CAMPAIGNS}/:campaignId/summary/*`,
    element: (
      <ProtectedRoute
        children={
          <PermittedPage permitted={permissions.GET_CAMPAIGN && permissions.READ_CAMPAIGN_LINEITEMS}>
            <CampaignSummary />
          </PermittedPage>
        }
      />
    ),
  },
  {
    path: `/:adAccountId${RoutePath.CAMPAIGNS}/lineItems/:lineItemId/summary/*`,
    element: (
      <ProtectedRoute
        children={
          <PermittedPage permitted={permissions.GET_LINE_ITEM}>
            <LineItemSummary />
          </PermittedPage>
        }
      />
    ),
  },
  {
    path: RoutePath.NOTIFICATIONS,
    element: <ProtectedRoute children={<Notifications />} />,
  },
  {
    path: `/oauth/callback`,
    element: <OAuthCallback />,
  },
  {
    path: `/secure`,
    element: (
      <Authentication checkInterval={5e3}>
        <User />
      </Authentication>
    ),
  },
];
